import { create } from "zustand";

export type AlertLiveStatus =
  | "idle"
  | "subscribing"
  | "connecting"
  | "live"
  | "polling"
  | "degraded";

/** Operator-facing wording for each channel status; kept next to the union. */
export const ALERT_LIVE_STATUS_LABEL: Record<AlertLiveStatus, string> = {
  idle: "Not connected",
  subscribing: "Connecting…",
  connecting: "Connecting…",
  live: "Live",
  polling: "Auto-refresh on",
  degraded: "Reconnecting…",
};

/** One human-readable line in the operator-visible live activity log. */
export interface AlertLiveActivity {
  id: string;
  at: number;
  kind: "hint" | "reconcile" | "connection" | "baseline" | "error";
  label: string;
  detail?: string;
}

/** Hard cap — this store lives for the whole session, so the log must be bounded. */
export const ACTIVITY_LOG_LIMIT = 50;
/** How long a hinted alert stays marked as freshly arrived. */
export const RECENT_HINT_TTL_MS = 60_000;

/**
 * A post-baseline Firestore hint, kept only as long as it can still authorise a
 * sound (see {@link RECENT_HINT_TTL_MS}).
 *
 * `signal` and `reviewCycle` are carried because MIN-223's sound policy is
 * unsatisfiable without them: sound requires `signal = attention` (condition 1)
 * AND a queue row whose `review_cycle` equals the hint's (condition 5). Storing
 * only the arrival time — as this did — makes both untestable at the gate.
 *
 * Identifiers stay STRINGS. Alert ids map to SQL bigints and may exceed
 * `Number.MAX_SAFE_INTEGER`, so they are never parsed into JS numbers; the
 * comparison against a REST row is always `String(row.id) === hint.alertId`.
 */
export interface AlertLiveHint {
  alertId: string;
  signal: "attention" | "cleared" | "info";
  reviewCycle: number;
  /** Null for alerts not bound to a machine — a machine filter must exclude those. */
  machineId: string | null;
  /** Epoch ms of arrival. */
  at: number;
}

interface AlertLiveStore {
  activeScopeKey: string | null;
  status: AlertLiveStatus;
  /** Derived exclusively from GET /api/alerts/queue/. Null means not hydrated yet. */
  queueCount: number | null;
  queueAsOf: string | null;
  lastReconciledAt: string | null;
  errorCode: string | null;
  /** Newest first, capped at ACTIVITY_LOG_LIMIT. */
  activity: AlertLiveActivity[];
  /** Alert id (STRING, per the Firestore contract) → the hint that named it. */
  recentHints: Record<string, AlertLiveHint>;
  beginScope: (scopeKey: string) => void;
  setStatus: (scopeKey: string, status: AlertLiveStatus, errorCode?: string | null) => void;
  acceptQueue: (scopeKey: string, count: number, asOf: string) => void;
  recordActivity: (scopeKey: string, entry: Omit<AlertLiveActivity, "id" | "at">) => void;
  recordHint: (scopeKey: string, hint: Omit<AlertLiveHint, "at">) => void;
  endScope: (scopeKey: string) => void;
}

let activitySeq = 0;

function nextActivityId(): string {
  activitySeq += 1;
  return `${Date.now()}-${activitySeq}`;
}

/** Drop hints older than the TTL so the map cannot grow unbounded. */
function pruneHints(
  hints: Record<string, AlertLiveHint>,
  now: number,
): Record<string, AlertLiveHint> {
  const cutoff = now - RECENT_HINT_TTL_MS;
  const next: Record<string, AlertLiveHint> = {};
  for (const [alertId, hint] of Object.entries(hints)) {
    if (hint.at >= cutoff) next[alertId] = hint;
  }
  return next;
}

export function alertLiveScopeKey(
  organizationId: number,
  locationId: number,
  machineIds: number[],
): string {
  const machines = [...new Set(machineIds)].sort((a, b) => a - b);
  return `${organizationId}:${locationId}:${machines.length ? machines.join(",") : "ALL"}`;
}

export const useAlertLiveStore = create<AlertLiveStore>((set, get) => ({
  activeScopeKey: null,
  status: "idle",
  queueCount: null,
  queueAsOf: null,
  lastReconciledAt: null,
  errorCode: null,
  activity: [],
  recentHints: {},

  beginScope: (scopeKey) => {
    if (get().activeScopeKey === scopeKey) return;
    set({
      activeScopeKey: scopeKey,
      status: "idle",
      queueCount: null,
      queueAsOf: null,
      lastReconciledAt: null,
      errorCode: null,
      activity: [],
      recentHints: {},
    });
  },

  setStatus: (scopeKey, status, errorCode = null) => {
    if (get().activeScopeKey !== scopeKey) return;
    set({ status, errorCode });
  },

  // A successful queue read is the canonical reconciliation point in v1
  // (there is no REST activity cursor to advance).
  acceptQueue: (scopeKey, count, asOf) => {
    if (get().activeScopeKey !== scopeKey) return;
    set({ queueCount: count, queueAsOf: asOf, lastReconciledAt: asOf });
  },

  // Additive instrumentation: the log is what an operator sees the channel doing.
  recordActivity: (scopeKey, entry) => {
    if (get().activeScopeKey !== scopeKey) return;
    const activity: AlertLiveActivity = { id: nextActivityId(), at: Date.now(), ...entry };
    set((state) => ({ activity: [activity, ...state.activity].slice(0, ACTIVITY_LOG_LIMIT) }));
  },

  recordHint: (scopeKey, hint) => {
    if (get().activeScopeKey !== scopeKey) return;
    const now = Date.now();
    set((state) => ({
      recentHints: {
        ...pruneHints(state.recentHints, now),
        [hint.alertId]: { ...hint, at: now },
      },
    }));
  },

  endScope: (scopeKey) => {
    if (get().activeScopeKey !== scopeKey) return;
    set({
      activeScopeKey: null,
      status: "idle",
      queueCount: null,
      queueAsOf: null,
      lastReconciledAt: null,
      errorCode: null,
      activity: [],
      recentHints: {},
    });
  },
}));
